import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isSkin, setRunnerSkin, SKINS, urlSkin, type Skin } from '../render/runnerArt';
import { launchInfo } from '../data/launch';

interface Prefs {
  reducedMotion: boolean;
  paused: boolean;
  labels: boolean;
  sound: boolean;
  skin: Skin;
  setReducedMotion(v: boolean): void;
  setPaused(v: boolean): void;
  setLabels(v: boolean): void;
  setSound(v: boolean): void;
  setSkin(v: Skin): void;
}

const Ctx = createContext<Prefs | null>(null);

function stored(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(`runner.${key}`);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}

function store(key: string, v: boolean | string) {
  try {
    localStorage.setItem(`runner.${key}`, typeof v === 'string' ? v : v ? '1' : '0');
  } catch {
    /* storage unavailable: preference lasts for this page only */
  }
}

function storedSkin(): Skin | null {
  try {
    const v = localStorage.getItem('runner.skin');
    return isSkin(v) ? v : null;
  } catch {
    return null;
  }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const systemReduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [reducedMotion, setRM] = useState(() => stored('reducedMotion', !!systemReduced));
  const [paused, setPaused] = useState(false);
  // Road callouts are opt-in: the road reads cleaner without them; the panels below carry the same detail.
  const [labels, setL] = useState(() => stored('labels', false));
  const [sound, setS] = useState(false); // decorative sound stays off until opt-in, never persisted on
  // Character look: ?skin= wins, then the viewer's own pick, then the event default in /launch.json.
  const [skin, setSk] = useState<Skin>(() => urlSkin ?? storedSkin() ?? 'runner');
  useEffect(() => store('reducedMotion', reducedMotion), [reducedMotion]);
  useEffect(() => store('labels', labels), [labels]);
  useEffect(() => setRunnerSkin(skin), [skin]);
  useEffect(() => {
    if (urlSkin || storedSkin()) return;
    let live = true;
    launchInfo().then((i) => { if (live && isSkin(i?.skin) && !storedSkin()) setSk(i.skin); });
    return () => { live = false; };
  }, []);
  const v = useMemo(
    () => ({
      reducedMotion, paused, labels, sound, skin, setReducedMotion: setRM, setPaused, setLabels: setL, setSound: setS,
      setSkin: (s: Skin) => { setSk(s); store('skin', s); },
    }),
    [reducedMotion, paused, labels, sound, skin],
  );
  return <Ctx.Provider value={v}>{children}</Ctx.Provider>;
}

export function usePrefs(): Prefs {
  const v = useContext(Ctx);
  if (!v) throw new Error('PrefsProvider missing');
  return v;
}

const SKIN_NAME: Record<Skin, string> = { runner: 'Runner', crab: 'Crab', roadster: 'Roadster' };
const SKIN_ICON: Record<Skin, string> = { runner: '🏃', crab: '🦀', roadster: '🚀' };

/** One button that cycles the character's look; the pick is remembered on this device. */
export function SkinPicker({ className }: { className?: string }) {
  const { skin, setSkin } = usePrefs();
  const next = SKINS[(SKINS.indexOf(skin) + 1) % SKINS.length];
  return (
    <button className={className ?? 'btn small ghost'} data-testid="skin-picker" onClick={() => setSkin(next)} aria-label={`Character look: ${SKIN_NAME[skin]}. Switch to ${SKIN_NAME[next]}`}>
      <span aria-hidden="true">{SKIN_ICON[skin]}</span> {SKIN_NAME[skin]}
    </button>
  );
}
